import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE from '../config';

function EditRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState(null);
  const [parentContact, setParentContact] = useState({ email: '', countryCode: '+39', phone: '' });
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState({ menus: [], relations: [] });

  // Delegation state
  const [delegationActive, setDelegationActive] = useState(false);
  const [delegation, setDelegation] = useState({
    delegatedStudent: { firstName: '', lastName: '', class: '' },
    delegatedParent: { firstName: '', lastName: '' }
  });

  const countryCodes = [
    { code: '+39', country: 'Italy' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+33', country: 'France' },
    { code: '+49', country: 'Germany' },
    { code: '+44', country: 'UK' },
    { code: '+34', country: 'Spain' },
    { code: '+41', country: 'Switzerland' },
    { code: '+43', country: 'Austria' },
    { code: '+32', country: 'Belgium' },
    { code: '+31', country: 'Netherlands' }
  ];

  useEffect(() => { fetchConfig(); fetchRegistration(); }, [token]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config`);
      setConfig(await response.json());
    } catch (err) { console.error('Failed to load config:', err); }
  };

  const isDeadlinePassed = () => {
    if (!config.registrationDeadline) return false;
    return new Date() > new Date(config.registrationDeadline);
  };

  const formatDeadline = () => {
    if (!config.registrationDeadline) return null;
    const d = config.registrationDeadline.substring(0, 10).split('-');
    return `${d[2]}/${d[1]}/${d[0]}`;
  };

  const fetchRegistration = async () => {
    try {
      const response = await fetch(`${API_BASE}/registrations/token/${token}`);
      if (!response.ok) { setError('Registration not found or link expired.'); setLoading(false); return; }
      const data = await response.json();
      setRegistration(data);
      const phoneParts = data.parentContact.phone.split('-');
      setParentContact({ email: data.parentContact.email, countryCode: phoneParts[0] || '+39', phone: phoneParts[1] || '' });
      setParticipants(data.participants);
      // Load delegation
      if (data.delegation && data.delegation.active) {
        setDelegationActive(true);
        setDelegation({
          delegatedStudent: { ...data.delegation.delegatedStudent },
          delegatedParent: { ...data.delegation.delegatedParent }
        });
      }
      setLoading(false);
    } catch (err) { setError('Failed to load registration.'); setLoading(false); }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{6,15}$/.test(phone);

  const updateParticipant = (index, field, value) => {
    const updated = [...participants];
    updated[index][field] = value;
    // Reset sibling fields when relation changes away from Fratello/Sorella
    if (field === 'relation' && value !== 'Fratello/Sorella') {
      updated[index].siblingStudent = false;
      updated[index].siblingClass = '';
    }
    setParticipants(updated);
  };

  const addParticipant = () => {
    setParticipants([...participants, { firstName: '', lastName: '', relation: config.relations[1] || 'Genitore', menu: config.menus[0]?.name || 'Hamburger', siblingStudent: false, siblingClass: '' }]);
  };

  const removeParticipant = (index) => {
    if (index === 0) return;
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const toggleDelegation = (active) => {
    setDelegationActive(active);
    if (active) {
      setParticipants(prev => [prev[0]]);
    }
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');

    if (!validateEmail(parentContact.email)) { setError('Inserire un indirizzo email valido.'); return; }
    if (!validatePhone(parentContact.phone)) { setError('Inserire un numero di telefono valido.'); return; }
    if (participants.some(p => !p.firstName.trim() || !p.lastName.trim())) { setError('Compilare nome e cognome di tutti i partecipanti.'); return; }

    // Validate sibling students
    for (const p of participants) {
      if (p.siblingStudent && p.relation === 'Fratello/Sorella') {
        if (!p.siblingClass) { setError(`Selezionare una classe per il fratello/sorella ${p.firstName} ${p.lastName}.`); return; }
        try {
          const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstName: p.firstName.trim(), lastName: p.lastName.trim(), siblingClass: p.siblingClass })
          });
          const sibData = await sibRes.json();
          if (!sibData.valid) {
            if (sibData.suggestion) {
              setError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
            } else {
              setError(sibData.error);
            }
            return;
          }
        } catch (err) { setError('Errore nella verifica del fratello/sorella.'); return; }
      }
    }

    if (delegationActive) {
      const ds = delegation.delegatedStudent;
      const dp = delegation.delegatedParent;
      if (!ds.firstName.trim() || !ds.lastName.trim() || !ds.class) { setError('Compilare tutti i campi dello studente delegato.'); return; }
      if (!dp.firstName.trim() || !dp.lastName.trim()) { setError('Compilare il nome del genitore delegato.'); return; }
      // Validate delegated student with fuzzy matching
      try {
        const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName: ds.firstName.trim(), lastName: ds.lastName.trim(), siblingClass: ds.class })
        });
        const sibData = await sibRes.json();
        if (!sibData.valid) {
          if (sibData.suggestion) {
            setError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
          } else {
            setError(sibData.error || 'Studente delegato non trovato.');
          }
          return;
        }
      } catch (err) { setError('Errore nella verifica dello studente delegato.'); return; }
    } else {
      const hasParent = participants.some(p => p.relation === 'Genitore' || p.relation === 'Altro Genitore');
      if (!hasParent) { setError('Almeno un genitore deve accompagnare il bambino, oppure abilitare la delega.'); return; }
    }

    try {
      const fullPhone = `${parentContact.countryCode}-${parentContact.phone}`;
      const delegationData = delegationActive
        ? { active: true, delegatedStudent: delegation.delegatedStudent, delegatedParent: delegation.delegatedParent }
        : { active: false };
      const response = await fetch(`${API_BASE}/registrations/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentContact: { ...parentContact, phone: fullPhone }, participants, delegation: delegationData })
      });
      if (response.ok) {
        setSuccess('Registrazione aggiornata con successo!');
        setTimeout(() => navigate('/'), 2000);
      } else {
        const data = await response.json();
        setError(data.error || 'Errore nel/nella aggiornamento della registrazione.');
      }
    } catch (err) { setError('Errore nel/nella aggiornamento della registrazione.'); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Sei sicuro di voler annullare questa registrazione? Questa azione non può essere annullata.')) return;
    try {
      const response = await fetch(`${API_BASE}/registrations/${token}`, { method: 'DELETE' });
      if (response.ok) { setSuccess('Registrazione annullata con successo.'); setTimeout(() => navigate('/'), 2000); }
      else { const data = await response.json(); setError(data.error || 'Errore nel/nella annullamento della registrazione.'); }
    } catch (err) { setError('Errore nel/nella annullamento della registrazione.'); }
  };

  if (loading) return <div className="container"><p>Caricamento...</p></div>;
  if (error && !registration) return (
    <div className="container">
      <div className="error">{error}</div>
      <button className="btn-secondary" onClick={() => navigate('/')}>Torna alla Home</button>
    </div>
  );

  return (
    <div className="container">
      <h2>Modifica Registrazione</h2>
      {isDeadlinePassed() && (
        <div className="warning-box" style={{ marginBottom: '20px' }}>
          ⚠️ Le registrazioni sono chiuse dal {formatDeadline()}. Non è più possibile effettuare modifiche.
        </div>
      )}
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Studente: <strong>{registration.student.firstName} {registration.student.lastName}</strong> - Classe {registration.student.class}
      </p>

      <div className="form-group">
        <label>Email del Genitore *</label>
        <input type="email" value={parentContact.email}
          onChange={(e) => setParentContact({ ...parentContact, email: e.target.value })} />
      </div>

      <div className="form-group">
        <label>Telefono del Genitore *</label>
        <div className="phone-input-group">
          <select value={parentContact.countryCode} className="country-code-select"
            onChange={(e) => setParentContact({ ...parentContact, countryCode: e.target.value })}>
            {countryCodes.map(item => <option key={item.code} value={item.code}>{item.code} ({item.country})</option>)}
          </select>
          <input type="tel" value={parentContact.phone} className="phone-number-input"
            onChange={(e) => setParentContact({ ...parentContact, phone: e.target.value.replace(/\D/g, '') })} />
        </div>
      </div>

      {/* Delegation Section */}
      <div className="delegation-section">
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={delegationActive}
              onChange={(e) => toggleDelegation(e.target.checked)} />
            Mio figlio sarà accompagnato dal genitore di un altro studente (delega)
          </label>
        </div>
        {delegationActive && (
          <div className="delegation-form">
            <h4>Studente Delegato</h4>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>Lo studente il cui genitore accompagnerà vostro figlio.</p>
            <div className="form-row">
              <div className="form-group">
                <label>Nome</label>
                <input type="text" value={delegation.delegatedStudent.firstName}
                  onChange={(e) => setDelegation({ ...delegation, delegatedStudent: { ...delegation.delegatedStudent, firstName: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Cognome</label>
                <input type="text" value={delegation.delegatedStudent.lastName}
                  onChange={(e) => setDelegation({ ...delegation, delegatedStudent: { ...delegation.delegatedStudent, lastName: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Classe</label>
                <select value={delegation.delegatedStudent.class}
                  onChange={(e) => setDelegation({ ...delegation, delegatedStudent: { ...delegation.delegatedStudent, class: e.target.value } })}>
                  <option value="">Seleziona classe</option>
                  {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <h4>Genitore Delegato</h4>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>Il genitore che accompagnerà vostro figlio.</p>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="form-group">
                <label>Nome</label>
                <input type="text" value={delegation.delegatedParent.firstName}
                  onChange={(e) => setDelegation({ ...delegation, delegatedParent: { ...delegation.delegatedParent, firstName: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Cognome</label>
                <input type="text" value={delegation.delegatedParent.lastName}
                  onChange={(e) => setDelegation({ ...delegation, delegatedParent: { ...delegation.delegatedParent, lastName: e.target.value } })} />
              </div>
            </div>
          </div>
        )}
      </div>

      <h3>Partecipanti</h3>
      <div className="participant-list">
        {participants.map((participant, index) => (
          <div key={index} className="participant-item">
            <div className="participant-header">
              <strong>Partecipante {index + 1}</strong>
              {index > 0 && <button className="btn-danger" onClick={() => removeParticipant(index)}>Rimuovi</button>}
            </div>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
              <div className="form-group">
                <label>Nome</label>
                <input type="text" value={participant.firstName} disabled={index === 0}
                  onChange={(e) => updateParticipant(index, 'firstName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Cognome</label>
                <input type="text" value={participant.lastName} disabled={index === 0}
                  onChange={(e) => updateParticipant(index, 'lastName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Relazione</label>
                <select value={participant.relation} disabled={index === 0}
                  onChange={(e) => updateParticipant(index, 'relation', e.target.value)}>
                  {config.relations.map(rel => {
                    const genitoreExists = participants.some((p, i) => i !== index && p.relation === 'Genitore');
                    const disabled = rel === 'Genitore' && genitoreExists && participant.relation !== 'Genitore';
                    return <option key={rel} value={rel} disabled={disabled}>{rel}{disabled ? ' (già presente)' : ''}</option>;
                  })}
                </select>
              </div>
              <div className="form-group">
                <label>Menù</label>
                <select value={participant.menu}
                  onChange={(e) => updateParticipant(index, 'menu', e.target.value)}>
                  {config.menus.map(menu => <option key={menu.name} value={menu.name}>{menu.name} (€{menu.price})</option>)}
                </select>
              </div>
            </div>
            {participant.relation === 'Fratello/Sorella' && index > 0 && (
              <div className="sibling-student-section">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={participant.siblingStudent || false}
                      onChange={(e) => updateParticipant(index, 'siblingStudent', e.target.checked)} />
                    Questo fratello/sorella è uno studente della scuola
                  </label>
                </div>
                {participant.siblingStudent && (
                  <div className="form-group" style={{ marginTop: '10px', maxWidth: '200px' }}>
                    <label>Classe del fratello/sorella</label>
                    <select value={participant.siblingClass || ''}
                      onChange={(e) => updateParticipant(index, 'siblingClass', e.target.value)}>
                      <option value="">Seleziona classe</option>
                      {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!delegationActive && (
        <button className="btn-secondary" onClick={addParticipant} style={{ marginRight: '10px' }}>
          Aggiungi Partecipante
        </button>
      )}

      {error && <div className="error" style={{ marginTop: '15px' }}>{error}</div>}
      {success && <div className="success" style={{ marginTop: '15px' }}>{success}</div>}

      <div style={{ marginTop: '15px', fontSize: '18px', fontWeight: 'bold' }}>
        Totale: €{participants.reduce((sum, p) => {
          const menuObj = config.menus.find(m => m.name === p.menu);
          return sum + (menuObj ? menuObj.price : 0);
        }, 0)}
      </div>

      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <button className="btn-primary" onClick={handleUpdate} disabled={isDeadlinePassed()}>Salva Modifiche</button>
        <button className="btn-danger" onClick={handleCancel} disabled={isDeadlinePassed()}>Annulla Registrazione</button>
        <button className="btn-secondary" onClick={() => navigate('/')}>Torna alla Home</button>
      </div>
    </div>
  );
}

export default EditRegistration;
